import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Field, TextArea, ToggleRow, EmptyState, Skeleton } from "@/components/admin/Field";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

const EMPTY = { title: "", description: "", icon: "", price_label: "", featured: false, sort_order: 0 };

export default function ServicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/services");
      setItems(data || []);
    } catch {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function startNew() {
    setDraft({ ...EMPTY, _isNew: true });
  }

  async function save() {
    if (!draft.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: draft.title,
        description: draft.description,
        icon: draft.icon,
        price_label: draft.price_label,
        featured: !!draft.featured,
        sort_order: Number(draft.sort_order) || 0,
      };
      if (draft._isNew) {
        const { data } = await api.post("/admin/services", payload);
        setItems((arr) => [...arr, data].sort((a, b) => a.sort_order - b.sort_order));
      } else {
        const { data } = await api.put(`/admin/services/${draft.id}`, payload);
        setItems((arr) => arr.map((s) => (s.id === data.id ? data : s)).sort((a, b) => a.sort_order - b.sort_order));
      }
      toast.success("Saved");
      setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/services/${pendingDelete.id}`);
      setItems((arr) => arr.filter((s) => s.id !== pendingDelete.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setPendingDelete(null);
    }
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#0055ff]">Services</span>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight text-white md:text-4xl">What we offer</h1>
          <p className="mt-1 font-body text-sm text-white/50">These appear in the Services section of the homepage.</p>
        </div>
        <button
          onClick={startNew}
          data-testid="services-new-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white transition hover:bg-[#1a66ff]"
        >
          <Plus className="h-4 w-4" /> New service
        </button>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : items.length === 0 && !draft ? (
        <EmptyState
          icon={Sparkles}
          title="No services yet"
          description="Add the disciplines your studio offers — they’ll appear publicly straight away."
          action={
            <button
              onClick={startNew}
              className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white"
            >
              <Plus className="h-4 w-4" /> New service
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((s) => (
            <article
              key={s.id}
              className="group rounded-3xl border border-white/[0.07] bg-white/[0.015] p-5 transition hover:border-white/15"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-body text-[10px] tracking-[0.25em] uppercase text-white/40">
                    {s.price_label || "—"}
                    {s.featured && <span className="text-[#7aa6ff]">· Featured</span>}
                  </div>
                  <h3 className="mt-1.5 font-display text-lg font-medium tracking-tight text-white">{s.title}</h3>
                  <p className="mt-1.5 line-clamp-3 font-body text-sm text-white/55">{s.description}</p>
                </div>
                <div className="flex shrink-0 flex-col gap-2">
                  <button
                    onClick={() => setDraft({ ...s, _isNew: false })}
                    className="rounded-full border border-white/12 px-3 py-1 font-body text-xs text-white/85 hover:border-white/25"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setPendingDelete(s)}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-white/12 text-white/55 hover:border-red-400/40 hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-[110] flex items-end justify-end bg-black/60 backdrop-blur-md sm:items-center sm:justify-center">
          <div className="w-full max-w-xl rounded-t-3xl border border-white/10 bg-[#0a0a0c] p-6 sm:rounded-3xl sm:p-8">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-display text-xl font-medium tracking-tight text-white">
                {draft._isNew ? "New service" : "Edit service"}
              </h3>
              <button onClick={() => setDraft(null)} className="text-white/55 hover:text-white">×</button>
            </div>
            <div className="flex flex-col gap-4">
              <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid="service-title" />
              <TextArea label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} rows={4} />
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Icon (label)" value={draft.icon} onChange={(v) => setDraft({ ...draft, icon: v })} placeholder="diamond" />
                <Field label="Price label" value={draft.price_label} onChange={(v) => setDraft({ ...draft, price_label: v })} placeholder="from €4.5k" />
              </div>
              <Field label="Sort order" value={draft.sort_order} onChange={(v) => setDraft({ ...draft, sort_order: v })} placeholder="0" />
              <ToggleRow
                label="Featured"
                description="Marks the service as a highlight."
                value={!!draft.featured}
                onChange={(v) => setDraft({ ...draft, featured: v })}
              />
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setDraft(null)} className="rounded-full border border-white/12 px-5 py-2.5 font-body text-sm text-white/75 hover:border-white/30 hover:text-white">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white hover:bg-[#1a66ff] disabled:opacity-60"
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this service?"
        description={`“${pendingDelete?.title || ""}” will be removed.`}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
